import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Lote = {
  id: number; recepcionId: number; codigoLote: string; fechaInicio: string; fechaFin: string | null;
  estado: string; kgObtenidos: number; costeado: boolean;
  recepcion: { loteCodigo: string; especieNombre: string; pesoNetoKg: number; precioCompraKg: number };
};
type Recepcion = { id: number; loteCodigo: string; especieNombre: string; pesoNetoKg: number; consumidaEnLote: boolean };
type Opcion = { id: number; nombre: string };

const ESTADO_BADGE: Record<string, string> = {
  abierto: "bg-slate-100 text-slate-500", en_proceso: "bg-amber-50 text-amber-700", cerrado: "bg-accent-light text-accent-dark",
};

export function Produccion() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("produccion:crear");
  const puedeEditar = tienePermiso("produccion:editar");
  const puedeCerrar = tienePermiso("produccion:cerrar");

  const [lotes, setLotes] = useState<Lote[]>([]);
  const [recepciones, setRecepciones] = useState<Recepcion[]>([]);
  const [especies, setEspecies] = useState<Opcion[]>([]);
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [loteSeleccionado, setLoteSeleccionado] = useState<number | null>(null);

  const [recepcionId, setRecepcionId] = useState("");
  const [codigoLote, setCodigoLote] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [l, r, cat, bod] = await Promise.all([
        api.get("/produccion/lotes"),
        api.get("/compras/recepciones"),
        api.get("/catalogos?tipo=especie"),
        api.get("/inventarios/bodegas"),
      ]);
      setLotes(l);
      setRecepciones(r);
      setEspecies(cat.map((c: any) => ({ id: c.id, nombre: c.nombre })));
      setBodegas(bod.map((b: any) => ({ id: b.id, nombre: b.nombre })));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar producción.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const recepcionesDisponibles = recepciones.filter((r) => !r.consumidaEnLote);

  const crearLote = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      const creado = await api.post("/produccion/lotes", { recepcionId: Number(recepcionId), codigoLote, fechaInicio });
      setModalAbierto(false);
      setRecepcionId(""); setCodigoLote(""); setFechaInicio("");
      await cargar();
      setLoteSeleccionado(creado.id);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo abrir el lote.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Shell titulo="Producción" breadcrumb="Producción">
      <p className="text-sm text-textmuted mb-4">
        Cada lote se abre a partir de una recepción de materia prima disponible y consume su inventario automáticamente.
      </p>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {loteSeleccionado ? (
        <DetalleLote
          loteId={loteSeleccionado}
          especies={especies}
          bodegas={bodegas}
          puedeEditar={puedeEditar}
          puedeCerrar={puedeCerrar}
          onVolver={() => { setLoteSeleccionado(null); cargar(); }}
        />
      ) : (
        <>
          <div className="flex justify-end mb-3">
            {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Abrir lote</button>}
          </div>
          {cargando ? (
            <p className="text-sm text-textmuted">Cargando...</p>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-borderc">
                    <th className="table-th">Lote</th><th className="table-th">Especie</th><th className="table-th">Inicio</th>
                    <th className="table-th">Estado</th><th className="table-th">Kg obtenidos</th><th className="table-th">Costeo</th><th className="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  {lotes.map((l) => (
                    <tr key={l.id} className="border-b border-borderc last:border-0">
                      <td className="table-td font-mono text-xs font-semibold">{l.codigoLote}</td>
                      <td className="table-td">{l.recepcion.especieNombre}</td>
                      <td className="table-td">{l.fechaInicio}</td>
                      <td className="table-td"><span className={`badge ${ESTADO_BADGE[l.estado]}`}>{l.estado}</span></td>
                      <td className="table-td">{l.kgObtenidos.toLocaleString()} kg</td>
                      <td className="table-td">
                        <span className={`badge ${l.costeado ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                          {l.costeado ? "calculado" : "pendiente"}
                        </span>
                      </td>
                      <td className="table-td text-right">
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => setLoteSeleccionado(l.id)}>Ver detalle</button>
                      </td>
                    </tr>
                  ))}
                  {lotes.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin lotes de producción.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crearLote} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Abrir lote de producción</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Recepción de origen</label>
              <select className="input-field" value={recepcionId} onChange={(e) => setRecepcionId(e.target.value)} required>
                <option value="">Seleccione...</option>
                {recepcionesDisponibles.map((r) => (
                  <option key={r.id} value={r.id}>{r.loteCodigo} · {r.especieNombre} · {r.pesoNetoKg} kg</option>
                ))}
              </select>
              {recepcionesDisponibles.length === 0 && (
                <p className="text-[11px] text-textmuted mt-1">No hay recepciones disponibles sin asignar a un lote. Registre una recepción primero en Compras.</p>
              )}
            </div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Código de lote</label>
              <input className="input-field font-mono" value={codigoLote} onChange={(e) => setCodigoLote(e.target.value)} placeholder="LOTE-0003" required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de inicio</label>
              <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required /></div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Abrir lote"}</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}

function DetalleLote({ loteId, especies, bodegas, puedeEditar, puedeCerrar, onVolver }: {
  loteId: number; especies: Opcion[]; bodegas: Opcion[]; puedeEditar: boolean; puedeCerrar: boolean; onVolver: () => void;
}) {
  const [lote, setLote] = useState<any>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tipoProceso, setTipoProceso] = useState("");
  const [rendimientoEsperado, setRendimientoEsperado] = useState("");
  const [prodEspecieId, setProdEspecieId] = useState("");
  const [productoObtenido, setProductoObtenido] = useState("");
  const [kgObtenidos, setKgObtenidos] = useState("");
  const [prodBodegaId, setProdBodegaId] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      setLote(await api.get(`/produccion/lotes/${loteId}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el lote.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [loteId]);

  const agregarProceso = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post(`/produccion/lotes/${loteId}/procesos`, { tipoProceso, rendimientoEsperadoPct: Number(rendimientoEsperado) });
      setTipoProceso(""); setRendimientoEsperado("");
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo registrar el proceso.");
    } finally {
      setEnviando(false);
    }
  };

  const agregarResultado = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      // El rendimiento real (%) y la merma del lote ya NO se digitan a
      // mano: el backend los calcula solo a partir del peso inicial de
      // materia prima de la recepción de origen y de la suma de kg
      // obtenidos de todos los resultados del lote (ver
      // _recalcular_rendimiento_y_merma en produccion_routes.py).
      await api.post(`/produccion/lotes/${loteId}/resultados`, {
        especieId: Number(prodEspecieId), productoObtenido, kgObtenidos: Number(kgObtenidos),
        bodegaId: Number(prodBodegaId),
      });
      setProdEspecieId(""); setProductoObtenido(""); setKgObtenidos(""); setProdBodegaId("");
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo registrar el resultado.");
    } finally {
      setEnviando(false);
    }
  };

  const cerrarLote = async () => {
    if (!window.confirm("¿Cerrar este lote? Ya no se podrán agregar más resultados ni mermas.")) return;
    setEnviando(true);
    try {
      await api.post(`/produccion/lotes/${loteId}/cerrar`, { fechaFin: new Date().toISOString().slice(0, 10) });
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cerrar el lote.");
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) return <p className="text-sm text-textmuted">Cargando...</p>;
  if (!lote) return null;

  return (
    <div>
      <button className="text-accent-dark hover:underline text-sm font-semibold mb-4" onClick={onVolver}>← Volver a lotes</button>
      <div className="card p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-800">{lote.codigoLote}</h2>
            <p className="text-sm text-textmuted mt-0.5">
              {lote.recepcion.especieNombre} · origen {lote.recepcion.loteCodigo} · {lote.recepcion.pesoNetoKg} kg a ${lote.recepcion.precioCompraKg}/kg
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`badge ${ESTADO_BADGE[lote.estado]}`}>{lote.estado}</span>
            {puedeCerrar && lote.estado !== "cerrado" && (
              <button className="btn-secondary text-xs" onClick={cerrarLote} disabled={enviando}>Cerrar lote</button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Procesos de transformación</h3>
          <ul className="space-y-2 mb-4">
            {lote.procesos.map((p: any) => (
              <li key={p.id} className="text-sm border-b border-borderc pb-2 last:border-0">
                <span className="font-medium">{p.tipo_proceso}</span> — rendimiento esperado {p.rendimiento_esperado_pct}%
              </li>
            ))}
            {lote.procesos.length === 0 && <li className="text-sm text-textmuted">Sin procesos registrados.</li>}
          </ul>
          {puedeEditar && lote.estado !== "cerrado" && (
            <form onSubmit={agregarProceso} className="space-y-2 border-t border-borderc pt-3">
              <input className="input-field" placeholder="Tipo de proceso (ej. Fileteado manual)" value={tipoProceso} onChange={(e) => setTipoProceso(e.target.value)} required />
              <input className="input-field" type="number" step="0.1" placeholder="Rendimiento esperado (%)" value={rendimientoEsperado} onChange={(e) => setRendimientoEsperado(e.target.value)} required />
              <button className="btn-secondary text-xs w-full" disabled={enviando}>+ Agregar proceso</button>
            </form>
          )}
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Mermas</h3>
          <p className="text-[11px] text-textmuted mb-3">
            Se calcula sola: peso inicial de materia prima menos el total de kg obtenidos en los resultados de este
            lote. Se actualiza cada vez que se agrega un resultado — no se digita a mano.
          </p>
          <ul className="space-y-2">
            {lote.mermas.map((m: any) => (
              <li key={m.id} className="text-sm border-b border-borderc pb-2 last:border-0">
                <span className="font-medium">{m.kg_merma} kg</span> — {m.motivo}
              </li>
            ))}
            {lote.mermas.length === 0 && <li className="text-sm text-textmuted">Sin merma (todo el peso inicial quedó en resultados registrados).</li>}
          </ul>
        </div>

        <div className="card p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Resultados de producción</h3>
          <table className="w-full mb-4">
            <thead><tr className="border-b border-borderc">
              <th className="table-th">Producto</th><th className="table-th">Especie</th><th className="table-th">Kg obtenidos</th><th className="table-th">Rendimiento real</th>
            </tr></thead>
            <tbody>
              {lote.resultados.map((r: any) => (
                <tr key={r.id} className="border-b border-borderc last:border-0">
                  <td className="table-td font-medium">{r.producto_obtenido}</td>
                  <td className="table-td">{r.especieNombre}</td>
                  <td className="table-td">{r.kg_obtenidos} kg</td>
                  <td className="table-td">{r.rendimiento_real_pct != null ? `${r.rendimiento_real_pct}%` : "—"}</td>
                </tr>
              ))}
              {lote.resultados.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-4">Sin resultados registrados.</td></tr>}
            </tbody>
          </table>
          {puedeEditar && lote.estado !== "cerrado" && (
            <form onSubmit={agregarResultado} className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-borderc pt-3">
              <select className="input-field" value={prodEspecieId} onChange={(e) => setProdEspecieId(e.target.value)} required>
                <option value="">Especie...</option>
                {especies.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
              <input className="input-field col-span-2" placeholder="Producto obtenido" value={productoObtenido} onChange={(e) => setProductoObtenido(e.target.value)} required />
              <input className="input-field" type="number" step="0.01" placeholder="Kg" value={kgObtenidos} onChange={(e) => setKgObtenidos(e.target.value)} required />
              <select className="input-field col-span-2" value={prodBodegaId} onChange={(e) => setProdBodegaId(e.target.value)} required>
                <option value="">Bodega destino...</option>
                {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
              <button className="btn-secondary text-xs col-span-2" disabled={enviando}>+ Agregar resultado</button>
              <p className="text-[10px] text-textmuted col-span-4">
                El rendimiento real (%) se calcula solo al guardar, sobre el peso inicial de materia prima del lote.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
